import { ChatsCircle, CircleNotch, HandPalm } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';
import type { GuidedInterviewBarState } from './guided-interview-bar';

/**
 * FRONT-2478: the guided interview's one exit, pinned above the Builder's
 * composer while an interview round is open. It replaced a quiet ghost link
 * in every question card's footer, which read as a footnote and scrolled
 * away with a long card. The accessible name of the button is unchanged from
 * that link on purpose — the browser specs find it by that name.
 */
export function GuidedInterviewBar({
  state,
  stopping,
  onStop,
}: {
  state: GuidedInterviewBarState;
  stopping: boolean;
  onStop: () => void;
}) {
  return (
    <div
      data-testid="guided-interview-bar"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-2xl border border-primary/45 bg-primary/[0.06] py-3 pl-4 pr-3',
        // Enter once, then a slow breathing halo; a static ring under reduced motion.
        '[animation:eddy-coding-panel-in_200ms_ease-out,eddy-interview-halo_2.4s_ease-in-out_200ms_infinite]',
        'motion-reduce:animate-none motion-reduce:shadow-[0_0_0_3px_color-mix(in_oklch,var(--primary)_12%,transparent)]',
      )}
    >
      <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2 text-[length:var(--text-sm)] font-semibold text-foreground">
          <ChatsCircle size={16} weight="fill" className="text-primary" aria-hidden />
          EDDY is interviewing you
          <span
            className="inline-flex items-center gap-1"
            role="img"
            aria-label={`Round ${state.round} of ${state.maxRounds}`}
          >
            {Array.from({ length: state.maxRounds }, (_, i) => (
              <span
                key={i}
                className={cn('h-1 w-3.5 rounded-full', i < state.round ? 'bg-primary' : 'bg-border')}
              />
            ))}
          </span>
        </div>
        <span className="text-[length:var(--text-xs)] text-muted-foreground text-pretty">
          {state.open
            ? 'A few questions so the first build is right. You can leave any time.'
            : 'EDDY is preparing the next question. You can leave any time.'}
        </span>
      </div>
      <Button
        type="button"
        size="sm"
        className="shrink-0 h-9 px-3.5 transition-transform active:scale-[0.96]"
        disabled={stopping}
        onClick={onStop}
        tooltip="Turn off guided questions for the rest of this session"
      >
        {stopping ? <CircleNotch size={14} className="animate-spin" /> : <HandPalm size={14} />}
        Stop asking, let&apos;s just talk
      </Button>
    </div>
  );
}
